import { createContext, useContext } from "react";

import type { WorkspaceArtifact, WorkspaceArtifactId } from "../types";
import type { ChangesFocus, EvidenceFocus } from "./workspaceTabsState";

/** What a chat card or inline link needs to hand an artifact to the panel. */
export interface WorkspaceTabsContextValue {
  combinedPrView?: boolean;
  /** Org of the session installation; links for other orgs stay plain. */
  org: string | undefined;
  /** Session on screen. Artifact permalinks build on it, not the URL, which lags a session switch. */
  sessionId?: string;
  /** Artifacts this conversation can show, by id. */
  artifacts: ReadonlyMap<WorkspaceArtifactId, WorkspaceArtifact>;
  /** Every Proof run, earlier rounds included, even when the pane lists no Proof tabs. */
  proofReports?: ReadonlyMap<
    WorkspaceArtifactId,
    Extract<WorkspaceArtifact, { kind: "proof_report" }>
  >;
  /** Repositories the Changes tab can diff; empty without a sandbox. */
  changesRepositories: ReadonlySet<string>;
  /** Open Changes on one file, scrolled to and highlighting its line where the diff shows it. */
  focusChange: (focus: ChangesFocus) => void;
  /** Open a report scrolled to one piece of its evidence. */
  focusEvidence: (focus: EvidenceFocus) => void;
  unseenArtifactIds: ReadonlySet<WorkspaceArtifactId>;
  openArtifact: (id: WorkspaceArtifactId) => void;
  /** Collapse the panel, keeping its tab arrangement. */
  closePanel: () => void;
  /** The open panel covers the chat column instead of docking beside it. */
  paneExpanded: boolean;
  /** The panel header's expand/dock toggle; absent where the panel overlays the chat and cannot expand. */
  toggleExpanded?: () => void;
}

/** Null where no conversation is mounted; inline links then stay plain permalinks. */
export const WorkspaceTabsContext =
  createContext<WorkspaceTabsContextValue | null>(null);

export function useWorkspaceTabsContext(): WorkspaceTabsContextValue | null {
  return useContext(WorkspaceTabsContext);
}

/**
 * Visible content: null while the panel is collapsed or evicted. Separate
 * from WorkspaceTabsContext so a tab switch re-renders only its readers.
 */
export const WorkspaceActiveArtifactContext =
  createContext<WorkspaceArtifactId | null>(null);

export function useActiveWorkspaceArtifactId(): WorkspaceArtifactId | null {
  return useContext(WorkspaceActiveArtifactContext);
}
